import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { flightApi } from '../utils/axiosConfig';
import { FlightDetails } from '../interface/flight.details';
import BaggageCarousel from '../components/BaggageCarousel';
import { DeviceDetails } from '../interface/device.details';
import { useSocket } from '../context/SocketContext';
import { useAdSequence } from '../hooks/useAdSequence';
import { AdvertisementType, RemarkToCodeMapping } from '@industronics/fids-utils';
import AdvertisementDisplay from '../components/AdvertisementDisplay';
import { apiRoutes } from '../utils/apiRoutes';
import { getData } from '../utils/api';
import { CARGO_SERVICE_TYPE_CODES } from '../utils/constants';
import { ADS_URL } from '../utils/env-utils';
import mahbLogo from '../assets/MAHB_LOGO.jpg';
import ScrollingText from '../components/scrolling/ScrollingText';

interface BaggageCarouselPageProp {
  device: DeviceDetails;
}

const ROWS = 2;
const FETCH_BUFFER = 2;

const DEFAULT_OFFSETS: Record<string, number> = {
  [RemarkToCodeMapping.CAROUSEL_CLOSED]: 20,
};

const DEFAULT_IMAGES = ['/1_eng.bmp', '/1_bm.bmp'];
const DEFAULT_SWITCH_INTERVAL = 10000;

const BaggageCarouselPage: React.FC<BaggageCarouselPageProp> = ({ device }) => {
  const [flights, setFlights] = useState<FlightDetails[]>([]);
  const [remarkOffsets, setRemarkOffsets] = useState<Record<string, number>>({});
  const remarkOffsetsRef = useRef(remarkOffsets);
  const [currentTime, setCurrentTime] = useState(moment());
  const [imgIndex, setImgIndex] = useState(0);

  const { socket } = useSocket();
  const { currentAd } = useAdSequence(device.advertisement);

  useEffect(() => {
    const id = setInterval(() => setCurrentTime(moment()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setImgIndex(prev => (prev + 1) % DEFAULT_IMAGES.length), DEFAULT_SWITCH_INTERVAL);
    return () => clearInterval(id);
  }, []);

  const isFullScreenAd =
    currentAd &&
    !currentAd.ratio &&
    currentAd.advertisementType === AdvertisementType.FullScreen &&
    currentAd.type === 'ad';

  // ─── Offset helpers ───────────────────────────────────────────────────────

  const getOffsetIfRemovable = useCallback((remarkKey?: string): number | undefined => {
    if (!remarkKey) return undefined;
    const offsets = remarkOffsetsRef.current;
    if (offsets[remarkKey] != null) return offsets[remarkKey];
    if (DEFAULT_OFFSETS[remarkKey] != null) return DEFAULT_OFFSETS[remarkKey];
    return undefined;
  }, []);

  const fetchRemovalOffsets = useCallback(async () => {
    try {
      const res = await getData<any>(apiRoutes.flightRemovalOffsets.getForDevice);
      if (res?.success && Array.isArray(res.data)) {
        const map: Record<string, number> = {};
        res.data.forEach((x: any) => {
          if (x?.remarkKey && Number.isFinite(x?.offsetMinutes)) {
            map[x.remarkKey] = x.offsetMinutes;
          }
        });
        remarkOffsetsRef.current = map;
        setRemarkOffsets(map);
      }
    } catch (e) {
      console.error('Failed to fetch removal offsets', e);
    }
  }, []);

  useEffect(() => { fetchRemovalOffsets(); }, [fetchRemovalOffsets]);

  // ─── Visibility filter ────────────────────────────────────────────────────

  const isFlightVisible = useCallback((item: FlightDetails): boolean => {
    if (item.isDeleted || !item.isDisplay) return false;
    const key = item?.carouselRemarks?.key;
    const offsetMinutes = getOffsetIfRemovable(key);
    if (offsetMinutes === undefined) return true;
    if (!item.carouselClosingTime) return true;
    const closedAtMs =
      (item as any)._cachedCarouselClosingTimeMs ??
      ((item as any)._cachedCarouselClosingTimeMs = new Date(item.carouselClosingTime).getTime());
    if (!Number.isFinite(closedAtMs)) return true;
    return closedAtMs + offsetMinutes * 60 * 1000 > Date.now();
  }, [getOffsetIfRemovable]);

  const sortByScheduled = (a: FlightDetails, b: FlightDetails): number => {
    const tA = (a as any)._cachedSortTime ?? ((a as any)._cachedSortTime = new Date(a.scheduledTravelTime).getTime());
    const tB = (b as any)._cachedSortTime ?? ((b as any)._cachedSortTime = new Date(b.scheduledTravelTime).getTime());
    if (tA !== tB) return tA - tB;
    return String(a._id).localeCompare(String(b._id));
  };

  // ─── Fetch flights ────────────────────────────────────────────────────────

  const fetchFlights = useCallback(async (retryCount = 0) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [2000, 5000, 10000];
    try {
      const res = await flightApi.get(apiRoutes.flightTrips.getFlightTrips, {
        params: { limit: ROWS + FETCH_BUFFER },
      });
      const data: FlightDetails[] = res.data.data || [];
      setFlights(
        data
          .filter(isFlightVisible)
          .filter((f) => !CARGO_SERVICE_TYPE_CODES.includes(f.serviceTypeCode))
          .sort(sortByScheduled)
      );
      console.log(`✅ Carousel flights fetched${retryCount > 0 ? ` (after ${retryCount} retries)` : ''}`);
    } catch (err: any) {
      if (err.response?.status === 401 || err.response?.status === 403) return;
      const isRetryable =
        !err.response || err.code === 'ECONNABORTED' ||
        err.response?.status >= 500 || err.response?.status === 429 || err.response?.status === 408;
      if (isRetryable && retryCount < MAX_RETRIES) {
        const delay = RETRY_DELAYS[retryCount] ?? RETRY_DELAYS[RETRY_DELAYS.length - 1];
        setTimeout(() => fetchFlights(retryCount + 1), delay);
      } else {
        console.error('❌ Failed to fetch carousel flights after all retries');
      }
    }
  }, [isFlightVisible]);

  useEffect(() => { fetchFlights(); }, []);

  // ─── Interval removal ─────────────────────────────────────────────────────

  useEffect(() => {
    const intervalId = setInterval(() => {
      setFlights((prev) => {
        const filtered = prev.filter(isFlightVisible);
        return filtered.length !== prev.length ? filtered : prev;
      });
    }, 60 * 1000);
    return () => clearInterval(intervalId);
  }, [isFlightVisible]);

  // ─── Socket ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!socket) return;

    const handleFlightUpdate = (message: FlightDetails) => {
      fetchFlights();
    };

    const onRemovalOffsetUpdate = () => {
      console.log('flightRemovalOffset:update — refreshing offsets');
      fetchRemovalOffsets();
    };

    const handleConnect = () => {
      socket.off('flight.update', handleFlightUpdate);
      socket.on('flight.update', handleFlightUpdate);
      fetchFlights();
    };

    socket.on('connect', handleConnect);
    socket.on('flightRemovalOffset:update', onRemovalOffsetUpdate);
    if (socket.connected) handleConnect();

    return () => {
      socket.off('connect', handleConnect);
      socket.off('flight.update', handleFlightUpdate);
      socket.off('flightRemovalOffset:update', onRemovalOffsetUpdate);
    };
  }, [socket, fetchFlights, fetchRemovalOffsets, isFlightVisible]);

  useEffect(() => {
      if (!socket) return;
  
      const handleBulkUpdate = (payload: {
          trigger: string;
          iataCode: string;
          cityName: string;
          translations: { lang: string; value: string }[];
          updatedFlightCount: number;
      }) => {
          console.log(`✈️ Bulk flight update — trigger: ${payload.trigger}, iataCode: ${payload.iataCode}, affected: ${payload.updatedFlightCount}`);
  
          setFlights(prevFlights =>
              prevFlights.map(flight => {
                  const hasStation = flight.stations?.some(
                      (s: any) => s.airport === payload.iataCode
                  );
                  if (!hasStation) return flight;
  
                  return {
                      ...flight,
                      stations: flight.stations.map((station: any) => {
                          if (station.airport !== payload.iataCode) return station;
                          return {
                              ...station,
                              city: payload.cityName,
                              translations: payload.translations,
                          };
                      }),
                  };
              })
          );
      };
  
      socket.on('flights.station.update', handleBulkUpdate);
  
      return () => {
          socket.off('flights.station.update', handleBulkUpdate);
      };
  }, [socket]);


useEffect(() => {
  if (!socket) return;

  const handleRemarkUpdate = (payload: {
    trigger: string;
    remarkCode: string;
    remarkName: string;
    airportLang: string;
    translations: { lang: string; value: string }[];
    updatedFlightCount: number;
  }) => {
    console.log(`✈️ Bulk remark update — trigger: ${payload.trigger}, remarkCode: ${payload.remarkCode}, affected: ${payload.updatedFlightCount}`);

    setFlights(prevFlights =>
      prevFlights.map(flight => {
        if (
          flight.remarkCode?.key !== payload.remarkCode &&
          flight.carouselRemarks?.key !== payload.remarkCode &&
          flight.checkInStatus?.key !== payload.remarkCode &&
          flight.currentStatus?.key !== payload.remarkCode
        ) return flight;

        // Backend already resolved: en + airport.language + all airlineLanguages + all stationLangs
        // Frontend filters down per-flight by its own stationLangs only
        const stationLangs = new Set(
          (flight.stations ?? []).flatMap((s: any) =>
            (s.translations ?? [])
              .filter((t: any) => t.lang !== 'en')
              .map((t: any) => t.lang)
          )
        );

        const filteredTranslations = payload.translations.filter(
          (t: any) => t.lang === 'en' || t.lang === flight?.airline?.language || t.lang === payload.airportLang || stationLangs.has(t.lang)
        );

        const patch: Partial<FlightDetails> = {};

        if (flight.remarkCode?.key === payload.remarkCode) {
          patch.remarkCode = { ...flight.remarkCode, description: payload.remarkName, translations: filteredTranslations };
        }
        if (flight.carouselRemarks?.key === payload.remarkCode) {
          patch.carouselRemarks = { ...flight.carouselRemarks, description: payload.remarkName, translations: filteredTranslations };
        }
        if (flight.checkInStatus?.key === payload.remarkCode) {
          patch.checkInStatus = { ...flight.checkInStatus, description: payload.remarkName, translations: filteredTranslations };
        }
        if (flight.currentStatus?.key === payload.remarkCode) {
          patch.currentStatus = { ...flight.currentStatus, description: payload.remarkName, translations: filteredTranslations };
        }

        return { ...flight, ...patch };
      })
    );
  };

  socket.on('flights.remark.update', handleRemarkUpdate);

  return () => {
    socket.off('flights.remark.update', handleRemarkUpdate);
  };
}, [socket]);

    useEffect(() => {
      if (!socket) return;
  
      const handleRefetch = (payload: { trigger: string }) => {
          console.log(`🔄 Flights refetch triggered — reason: ${payload.trigger}`);
          fetchFlights();
      };
  
      socket.on('flights.refetch', handleRefetch);
      return () => { socket.off('flights.refetch', handleRefetch); };
    }, [socket]);

  // ─── Time window filter ─────────────────────────────────────────────────
  const windowedFlights = useMemo(() => {
    const timeWindowHours = device.cluster?.settings?.flightTimeWindowInHour;
    if (timeWindowHours == null || timeWindowHours <= 0) return flights;

    const cutoff = currentTime.valueOf() + timeWindowHours * 60 * 60 * 1000;
    return flights.filter(f => {
      const scheduledMs = new Date(f.scheduledTravelTime).getTime();
      return !Number.isFinite(scheduledMs) || scheduledMs <= cutoff;
    });
  }, [flights, currentTime, device.cluster?.settings?.flightTimeWindowInHour]);

  // Periodic refetch — ensures flights entering the backend's time window are picked up
  useEffect(() => {
    if (!device.cluster?.settings?.flightTimeWindowInHour) return;
    const intervalId = setInterval(() => { fetchFlights(); }, 60_000);
    return () => clearInterval(intervalId);
  }, [device.cluster?.settings?.flightTimeWindowInHour, fetchFlights]);

  const clusterDefaultImage = device.cluster?.settings?.defaultDisplayImage
    ? `${ADS_URL}/cluster-settings/${device.cluster.settings.defaultDisplayImage}`
    : null;

  const dateStr = currentTime.utcOffset(8).format('MMM DD, ddd');

  // ─── Render ───────────────────────────────────────────────────────────────

  const renderNoFlights = () => (
    <div className="flight-board-container" style={{ '--total-rows': ROWS, height: '100vh', display: 'flex', flexDirection: 'column' } as React.CSSProperties}>
      <div className="bc-header">
        <span className="bc-header-carousel">
          <span className="bc-header-carousel-label">CAROUSEL</span>{' '}
          <span className="bc-header-carousel-bold">{(device.assignment ?? '').trim().toUpperCase()}</span>
        </span>
        <span className="bc-header-date">{dateStr}</span>
      </div>
      <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
        {clusterDefaultImage ? (
          <img
            src={clusterDefaultImage}
            alt="Default display"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'fill' }}
          />
        ) : (
          DEFAULT_IMAGES.map((src, i) => (
            <img
              key={src}
              src={src}
              alt="Default display"
              style={{
                position: 'absolute', inset: 0, width: '100%', height: '100%',
                objectFit: 'fill', opacity: i === imgIndex ? 1 : 0, transition: 'opacity 1s ease-in-out',
              }}
            />
          ))
        )}
      </div>
      <div className="bc-footer">
        {device.announcement?.messageContent?.lines?.length ? (
          <div className="bc-footer-marquee">
            <ScrollingText
              sentences={device.announcement.messageContent.lines}
              speed={device.announcement.messageContent.speed || 50}
              separator=" &bull; "
              backgroundColor={device.announcement.messageContent.backgroundColor ?? 'transparent'}
              textColor={device.announcement.messageContent.textColor ?? 'white'}
              fontFamily={device.announcement.messageContent.fontFamily ?? 'Arial, sans-serif'}
              fontSize={device.announcement.messageContent.fontSize ?? 16}
              noScroll={device.announcement.messageContent.noScroll ?? false}
            />
          </div>
        ) : (
          <div className="bc-footer-marquee" />
        )}
        <div className="bc-footer-divider" />
        <div className="bc-footer-logo">
          <img src={mahbLogo} alt="MAHB Logo" className="bc-footer-logo-img" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="baggage-page-container">
      {isFullScreenAd ? (
        <AdvertisementDisplay advertisement={currentAd} isFullScreen={true} />
      ) : windowedFlights.length === 0 ? (
        renderNoFlights()
      ) : (
        <>
          <BaggageCarousel flights={windowedFlights.slice(0, ROWS)} totalRows={ROWS} carouselName={device.assignment} announcement={device.announcement} />
          {currentAd && currentAd.ratio && (
            <AdvertisementDisplay advertisement={currentAd} isFullScreen={false} />
          )}
        </>
      )}
    </div>
  );
};

export default BaggageCarouselPage;